import { useCallback, useEffect } from "react";
import useSWRInfinite from "swr/infinite";
import { apiClient } from "~/utils/apiClient";

/** The `PaginatedResponse[T]` envelope every offset/limit list endpoint returns. */
export interface PageMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  metadata: PageMetadata;
}

export interface UseServerListOptions {
  /** Base list URL, e.g. `agentListPath()`. Query params are appended. */
  path: string;
  /** Search term sent as `?q=`; blank is omitted. Debounce before passing. */
  search?: string;
  pageSize?: number;
}

export interface UseServerListResult<T> {
  items: T[];
  /** Total rows matching the query server-side, independent of how many pages
   *  are loaded. */
  total: number;
  hasMore: boolean;
  /** The first page is still loading. */
  isLoading: boolean;
  /** A `loadMore()` page is in flight. */
  isLoadingMore: boolean;
  error: unknown;
  loadMore: () => void;
  /** Re-fetch the pages already requested — including one whose fetch errored —
   *  without advancing the offset, so a failed `loadMore()` can be retried
   *  without leaving a gap. */
  retry: () => void;
}

const DEFAULT_PAGE_SIZE = 24;

/**
 * Server-driven "Load More" list over an offset/limit endpoint returning the
 * `PaginatedResponse[T]` envelope. Wraps `useSWRInfinite`: each `loadMore()`
 * appends the next offset page, and the accumulated `items` span every loaded
 * page. Changing `search` resets paging to the first page.
 *
 * Keeps the offset contract the backend already speaks — no cursor — so it can
 * back any list endpoint (agents today, projects next) by passing a different
 * `path`. `total`/`hasMore` come straight from the envelope metadata.
 */
export function useServerList<T>({
  path,
  search = "",
  pageSize = DEFAULT_PAGE_SIZE,
}: UseServerListOptions): UseServerListResult<T> {
  const term = search.trim();

  const getKey = (index: number, previous: PaginatedResponse<T> | null) => {
    // Stop once the server reports the previous page was the last one.
    if (previous && !previous.metadata.has_more) {
      return null;
    }
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String(index * pageSize),
    });
    if (term) {
      params.set("q", term);
    }
    // `path` may already carry a query (e.g. `?view=editable`), so join with the
    // right separator rather than assuming a bare path.
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}${params.toString()}`;
  };

  const { data, size, setSize, isLoading, error, mutate } = useSWRInfinite<PaginatedResponse<T>>(
    getKey,
    (url: string) => apiClient<PaginatedResponse<T>>(url),
    // `useSWRInfinite` defaults `revalidateOnMount` to false (unlike plain `useSWR`,
    // which defaults to true) — without this, a list that already has cached data
    // never refetches on remount, so edits made elsewhere (e.g. a rename) only show
    // up after a hard refresh.
    { revalidateFirstPage: false, revalidateOnMount: true }
  );

  // A new query or a new base `path` (e.g. a changed view filter) is a fresh
  // list, not a continuation — collapse back to one page so a deep scroll on the
  // prior list doesn't over-fetch the new results.
  useEffect(() => {
    setSize(1);
  }, [path, term, setSize]);

  const pages = data ?? [];
  const items = pages.flatMap((page) => page.items);
  const last = pages.at(-1);

  const loadMore = useCallback(() => {
    setSize((current) => current + 1);
  }, [setSize]);

  const retry = useCallback(() => {
    void mutate();
  }, [mutate]);

  return {
    items,
    total: pages[0]?.metadata.total_count ?? 0,
    hasMore: last?.metadata.has_more ?? false,
    isLoading,
    // A requested page (index `size - 1`) has no data yet — a load-more in
    // flight, distinct from the initial load. An errored page is also undefined,
    // so exclude `error` to avoid a button stuck in the loading state.
    isLoadingMore: !isLoading && !error && !!data && data[size - 1] === undefined,
    error,
    loadMore,
    retry,
  };
}
